import { useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col, DatePicker } from "antd";
import { FormContent } from "../../../components/form/form";
import { TDropdownOptions, TResponseModel, TUser } from "../../../types/config";
import { eLOCATIONTYPE, eResultCode, eTICKETTYPE } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import styles from "./ticket.module.css";
import CustomDropdown from "../../../components/custom-dropdown/custom-dropdown";
import {
  AddEditDealer,
  GetConfigParamDataList,
  GetCountryList,
  GetCustMappedPartnerList,
  GetProductList,
  GetStateList,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { CONFIG_GROUPS } from "../../../utils/constants";
import { TDealer, TProduct } from "../../../types";
import "./ticket.module.css";
import { requiredFieldValidator } from "../../../utils/input.validator";
import DateUtils from "../../../utils/date";
import dayjs from "dayjs";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type TProps = {
  onClose: Function;
  fetchNewDealerDetails: Function;
  ticketType?: number | null;
  selectedCustomer: number | null;
};

export default function NewDealerForm(props: TProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [countryOptions, setCountryOptions] = useState<TDropdownOptions[]>([]);
  const [stateOptions, setStateOptions] = useState<TDropdownOptions[]>([]);
  const [regionOptions, setRegionOptions] = useState<TDropdownOptions[]>([]);

  const [locationTypes, setLocationTypes] = useState<TDropdownOptions[]>([]);
  // const [slaOptions, setSLAOptions] = useState<TDropdownOptions[]>([]);

  // Hook to handle on load operations
  useEffect(() => {
    /**
     * fetchData is an asynchronous function that handles the initial loading of data
     * for the DealerForm component. It sets the loading state to true, and then
     * sequentially fetches the specific dealer data (if an ID is provided),
     * a list of countries, states, regions, and location types. The function
     * updates the corresponding state variables with the fetched data and
     * finally sets the loading state to false.
     */
    const fetchData = async () => {
      setIsLoading(true);
      await fetchCountry(); // Fetch countries
      await fetchStates(); // Fetch states
      const regions = await fetchConfigParams(CONFIG_GROUPS.REGION);
      setRegionOptions(regions);
      const locations = await fetchConfigParams(CONFIG_GROUPS.LOCATIONTYPE);
      setLocationTypes(locations);

      // const sla = await fetchConfigParams(CONFIG_GROUPS.SLAOPTIONS);
      // setSLAOptions(sla);
      setIsLoading(false);
    };

    fetchData(); // Call the fetch function
  }, []);

  // To fetch specific config param

  // To get the list of countries
  const fetchCountry = async () => {
    try {
      const payload = {
        data: {},
      };

      const response: TResponseModel = await post(GetCountryList, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedData = Utils.mapDropdownOptions(data);
          form.setFieldValue("countryId", mappedData[0].value);
          setCountryOptions(mappedData);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  // To get the list of states
  const fetchStates = async () => {
    try {
      const payload = {
        data: {
          countryId: form.getFieldValue("countryId"),
        },
      };

      const response: TResponseModel = await post(GetStateList, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          setStateOptions(Utils.mapDropdownOptions(data));
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  // To get the list of parameter options
  const fetchConfigParams = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          groupUniqueId,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          return Utils.mapDropdownOptions(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  // Handle on submit event
  const onHandleFinish = async (values: TDealer) => {
    try {
      const {
        sla,
        installationDate,
        amcToDate,
        amcFromDate,
        warrantyDate,
        lastPM,
        customerId,
        ...restValues
      } = values;

      const payload = {
        data: {
          id: 0,
          customerId: props.selectedCustomer,
          sla: sla * 1,
          warrantyDate: warrantyDate
            ? DateUtils.getDateStringWithOutTimezone(
                new Date(warrantyDate).toDateString()
              )
            : null,
          amcToDate: amcToDate
            ? DateUtils.getDateStringWithOutTimezone(
                new Date(amcToDate).toDateString()
              )
            : null,
          amcFromDate: amcFromDate
            ? DateUtils.getDateStringWithOutTimezone(
                new Date(amcFromDate).toDateString()
              )
            : null,
          installationDate: installationDate
            ? DateUtils.getDateStringWithOutTimezone(
                new Date(installationDate).toDateString()
              )
            : null,
          lastPM: lastPM
            ? DateUtils.getDateStringWithOutTimezone(
                new Date(lastPM).toDateString()
              )
            : null,
          ...restValues,
        },
      };

      const response: TResponseModel = await post(AddEditDealer, payload);
      const { data, dataResponse } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        props.fetchNewDealerDetails(data);
        form.resetFields();
        props.onClose();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
    }
  };

  /**
   * Resets the district field and fetches the list of cities based on the selected state
   */

  // Hook to handle on load operations

  return (
    <div>
      <Form
        form={form}
        name="registerDealer"
        onFinish={onHandleFinish}
        layout="vertical"
        autoComplete="off"
      >
        <FormContent className={styles.contentStyle}>
          <div
            style={{ overflowX: "hidden", height: "68vh", overflowY: "scroll" }}
          >
            <Row gutter={16} className="">
              <Col span={8}>
                <Form.Item
                  name="servicePartnerId"
                  label={
                    <span className={styles.customLabel}>Service Partner</span>
                  }
                  rules={[
                    { required: true, message: "Please choose customer" },
                  ]}
                >
                  <CustomDropdown
                    endPoint={GetCustMappedPartnerList}
                    value={form.getFieldValue("servicePartnerId")}
                    selectedName={form.getFieldValue("servicePartnerName")}
                    onChange={(selected: any) => {
                      form.setFieldValue("servicePartnerId", selected.value);
                      form.setFieldValue("servicePartnerName", selected.label);
                    }}
                    label=""
                    placeHolder="Select service partner"
                    addPaylod={{
                      customerId: props.selectedCustomer,
                    }}
                  ></CustomDropdown>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="depotCode"
                  label={<span className={styles.customLabel}>Depot Code</span>}
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("enter depot code"),
                    },
                  ]}
                >
                  <Input autoComplete="off" placeholder="Enter depot code" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="dealerCode"
                  label={
                    <span className={styles.customLabel}>Dealer Code</span>
                  }
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("enter dealer code"),
                    },
                  ]}
                >
                  <Input autoComplete="off" placeholder="Enter dealer code" />
                </Form.Item>
              </Col>
              {/* Dealer Name */}
              <Col span={8}>
                <Form.Item
                  name="dealerName"
                  label={
                    <span className={styles.customLabel}>Dealer Name</span>
                  }
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("enter dealer name"),
                    },
                  ]}
                >
                  <Input autoComplete="off" placeholder="Enter dealer name" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="address"
                  label={<span className={styles.customLabel}>Address</span>}
                  rules={[
                    {
                      required: true,
                      message: "Please enter address",
                      validator: requiredFieldValidator("enter dealer address"),
                    },
                  ]}
                >
                  <Input
                    autoComplete="off"
                    placeholder="Enter dealer address"
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="countryId"
                  label={<span className={styles.customLabel}>Country</span>}
                  rules={[{ required: true, message: "Please choose country" }]}
                >
                  <Select
                    placeholder="Select country"
                    options={countryOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="districtName"
                  label={<span className={styles.customLabel}>City</span>}
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("enter city"),
                    },
                  ]}
                >
                  <Input autoComplete="off" placeholder="Enter city" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="zipcode"
                  label={<span className={styles.customLabel}>Pin Code</span>}
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("Enter pincode"),
                    },
                    {
                      pattern: /^[0-9]{6}$/,
                      message: "Pincode must be a 6-digit number",
                    },
                  ]}
                >
                  <Input
                    autoComplete="off"
                    maxLength={6}
                    placeholder="Enter pincode"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="stateId"
                  label={<span className={styles.customLabel}>State</span>}
                  rules={[{ required: true, message: "Please choose state" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select state"
                    options={stateOptions}
                    // onChange={handleStateChange}
                    filterOption={(input, option) =>
                      option?.label
                        ? option.label
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        : false
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="regionName"
                  label={<span className={styles.customLabel}>DIV/Region</span>}
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator("Enter region"),
                    },
                  ]}
                >
                  <Input autoComplete="off" placeholder="Enter DIV/Region" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="contactNo"
                  label={
                    <span className={styles.customLabel}>
                      Dealer Contact No.
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please enter dealer contact no",
                    },
                    {
                      pattern: /^\d{10}$/,
                      message: "Phone number must be exactly 10 digits!",
                    },
                  ]}
                >
                  <Input
                    autoComplete="off"
                    placeholder="Enter dealer contact no"
                    maxLength={10}
                  />
                </Form.Item>
              </Col>
              {props?.ticketType === eTICKETTYPE.SERVICEREQUEST ? (
                <>
                  <Col span={8}>
                    <Form.Item
                      name="installationDate"
                      label={
                        <span className={styles.customLabel}>
                          Date of Installation
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please select installation date",
                        },
                      ]}
                    >
                      <DatePicker
                        maxDate={dayjs(new Date())}
                        format="DD-MMM-YYYY"
                        placeholder="Select installation date"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="warrantyDate"
                      label={
                        <span className={styles.customLabel}>
                          Warranty Expiry Date
                        </span>
                      }
                    >
                      <DatePicker
                        format="DD-MMM-YYYY"
                        // value={form.getFieldValue("amcFromDate")}
                        placeholder="Select warranty date"
                      />
                    </Form.Item>
                  </Col>{" "}
                </>
              ) : null}
              {/* Date of Installation */}

              {/* Machine Type */}
              <Col span={8}>
                <Form.Item
                  name="machineTypeName"
                  label={
                    <span className={styles.customLabel}>Machine Type</span>
                  }
                  rules={[
                    { required: true, message: "Please enter machine type" },
                  ]}
                >
                  <Select
                    placeholder="Select machine type"
                    options={[
                      { label: "Dispenser", value: "Dispenser" },
                      { label: "Mixer", value: "Mixer" },
                      { label: "Shaker", value: "Shaker" },
                    ]}
                  />
                </Form.Item>
              </Col>
              {/* Product */}
              <Col span={8}>
                <Form.Item
                  name="productId"
                  label={<span className={styles.customLabel}>Model</span>}
                  rules={[{ required: true, message: "Please choose model" }]}
                >
                  <CustomDropdown
                    endPoint={GetProductList}
                    value={form.getFieldValue("productId")}
                    selectedName={form.getFieldValue("productName")}
                    onChange={(selected: TProduct) => {
                      form.setFieldValue("productId", selected.value);
                      form.setFieldValue("productName", selected.label);
                      form.setFieldValue(
                        "machineModelName",
                        selected.machineModelName
                      );
                      // form.setFieldValue(
                      //   "machineTypeName",
                      //   selected.machineTypeName
                      // );
                    }}
                    optionId="id"
                    optionName="productName"
                    label=""
                    placeHolder="Select model"
                  ></CustomDropdown>
                </Form.Item>
              </Col>

              {/* Machine Serial No */}
              <Col span={8}>
                <Form.Item
                  name="machineSerialNo"
                  label={
                    <span className={styles.customLabel}>
                      Machine Serial No
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      validator: requiredFieldValidator(
                        "enter machine serial number"
                      ),
                    },
                  ]}
                >
                  <Input
                    autoComplete="off"
                    placeholder="Enter machine serial number"
                  />
                </Form.Item>
              </Col>
              {/* Gyro YES/NO */}
              <Col span={8}>
                <Form.Item
                  name="gyro"
                  label={<span className={styles.customLabel}>Gyro</span>}
                  rules={[
                    { required: true, message: "Please select Gyro status" },
                  ]}
                >
                  <Select
                    placeholder="Select Gyro status"
                    options={[
                      { label: "YES", value: 1 },
                      { label: "NO", value: 0 },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="gyroType"
                  label={<span className={styles.customLabel}>Gyro Type</span>}
                  rules={[
                    {
                      required: true,
                      message: "Please Enter Gyro Type",
                    },
                  ]}
                >
                  <Input autoComplete="off" placeholder="Enter Gyro Type" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="ups"
                  label={<span className={styles.customLabel}>UPS</span>}
                  rules={[{ required: true, message: "Please select UPS" }]}
                >
                  <Select
                    placeholder="Select UPS"
                    options={[
                      { label: "YES", value: 1 },
                      { label: "NO", value: 0 },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="galileo"
                  label={<span className={styles.customLabel}>Galileo</span>}
                  rules={[{ required: true, message: "Please select Galileo" }]}
                >
                  <Select
                    placeholder="Select Galileo"
                    options={[
                      { label: "YES", value: 1 },
                      { label: "NO", value: 0 },
                    ]}
                  />
                </Form.Item>
              </Col>

              {/* Gyro SN */}
              <Col span={8}>
                <Form.Item
                  name="gyroSerialNo"
                  label={<span className={styles.customLabel}>Gyro SN</span>}
                  dependencies={["gyro"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      required: getFieldValue("gyro") === 1,
                      message: "Please enter Gyro Serial Number",
                    }),
                  ]}
                >
                  <Input
                    autoComplete="off"
                    placeholder="Enter Gyro serial number"
                  />
                </Form.Item>
              </Col>

              {/* Location Type */}
              <Col span={8}>
                <Form.Item
                  name="locationTypeId"
                  label={
                    <span className={styles.customLabel}>Location Type</span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select location type",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select location type"
                    options={locationTypes}
                    onChange={(value: number, option) => {
                      if (
                        (option as TDropdownOptions)?.data?.paramUniqueId ===
                        eLOCATIONTYPE.LOCAL
                      ) {
                        form.setFieldValue("sla", 24);
                      } else if (
                        (option as TDropdownOptions)?.data?.paramUniqueId ===
                        eLOCATIONTYPE.REMOTE
                      ) {
                        form.setFieldValue("sla", 72);
                      } else if (
                        (option as TDropdownOptions)?.data?.paramUniqueId ===
                        eLOCATIONTYPE.UPCOUNTRY
                      ) {
                        form.setFieldValue("sla", 48);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              {/* SLA/TAT */}
              <Col span={8}>
                <Form.Item
                  name="sla"
                  label={
                    <span className={styles.customLabel}>SLA (in hours)</span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select sla",
                    },
                  ]}
                  dependencies={["locationTypeId"]}
                >
                  <Select
                    placeholder="Select sla"
                    options={[
                      { value: 24, label: "24" },
                      { value: 48, label: "48" },
                      { value: 72, label: "72" },
                    ]}
                  />
                </Form.Item>
              </Col>
              {props?.ticketType === eTICKETTYPE.SERVICEREQUEST ? (
                <>
                  {" "}
                  <Col span={8}>
                    <Form.Item
                      name="amcFromDate"
                      label={
                        <span className={styles.customLabel}>AMC From</span>
                      }
                      dependencies={["isAMCActive", "amcToDate"]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const amcToDate = getFieldValue("amcToDate");
                            if (
                              value &&
                              amcToDate &&
                              value.isAfter(amcToDate)
                            ) {
                              return Promise.reject(
                                new Error(
                                  "AMC From Date cannot be greater than AMC To Date"
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        disabled={true}
                        format="DD-MMM-YYYY"
                        placeholder="Select AMC from date"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="amcToDate"
                      label={<span className={styles.customLabel}>AMC To</span>}
                      dependencies={["isAMCActive", "amcFromDate"]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const amcFromDate = getFieldValue("amcFromDate");
                            if (
                              value &&
                              amcFromDate &&
                              value.isBefore(amcFromDate)
                            ) {
                              return Promise.reject(
                                new Error(
                                  "AMC To Date cannot be less than AMC From Date"
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        disabled={true}
                        format="DD-MMM-YYYY"
                        placeholder="Select AMC to date"
                      />
                    </Form.Item>
                  </Col>
                  {/* last pm */}
                  <Col span={8}>
                    <Form.Item
                      name="lastPM"
                      label={
                        <span className={styles.customLabel}>Last PM Date</span>
                      }
                      // rules={[
                      //   {
                      //     required: true,
                      //     message:
                      //       "Please select last preventive maintenance date",
                      //   },
                      // ]}
                    >
                      <DatePicker
                        disabled={true}
                        format="DD-MMM-YYYY"
                        maxDate={dayjs(new Date())}
                        placeholder="Select last PM date"
                      />
                    </Form.Item>
                  </Col>
                  {/* is active */}
                  <Col span={8}>
                    <Form.Item
                      name="isActive"
                      label={<span className={styles.customLabel}>Status</span>}
                      // rules={[
                      //   { required: true, message: "Please select status" },
                      // ]}
                    >
                      <Select
                        disabled={true}
                        value={0}
                        defaultValue={0}
                        placeholder="Select status"
                        options={[
                          { label: "Active", value: 1 },
                          { label: "Inactive", value: 0 },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </>
              ) : null}
              {/* contact name */}
              <Col span={8}>
                <Form.Item
                  name="contactName"
                  label={
                    <span className={styles.customLabel}>Contact Name</span>
                  }
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Please enter contact person name",
                  //   },
                  // ]}
                >
                  <Input
                    autoComplete="off"
                    placeholder="Enter contact person name"
                  />
                </Form.Item>
              </Col>
              {/* dealer email */}
              <Col span={8}>
                <Form.Item
                  name="dealerEmail"
                  label={
                    <span className={styles.customLabel}>Dealer Email</span>
                  }
                  // rules={[
                  //   { required: true, message: "Please enter dealer email" },
                  // ]}
                >
                  <Input autoComplete="off" placeholder="Enter dealer email" />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <Row
            gutter={16}
            style={{ justifyContent: "center", marginBottom: "-1rem" }}
          >
            <div>
              <Button
                disabled={form.getFieldValue("customerId") ? true : false}
                type="primary"
                onClick={form.submit}
              >
                Save
              </Button>
            </div>
            <Col span={2}>
              <Button
                type="default"
                onClick={() => props.onClose()}
                disabled={isLoading}
              >
                Close
              </Button>
            </Col>
          </Row>
        </FormContent>

        {/* <FormFooter className={styles.footerStyle}>
          <FormRow>
          
          </FormRow>
        </FormFooter> */}
      </Form>
    </div>
  );
}
